import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../utils/api";
import styless from '../../component/bomComps/BomListTable/bomListTable.module.css';

// Popup to pick one or more vendors (with search) and send the selected BOM parts to all of them
const BomVendorPicker = ({ enquiryId, enquiryType = 'BOM', parts = [], onClose, onSent }) => {
    const [search, setSearch] = useState('');
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [selectedVendors, setSelectedVendors] = useState([]);

    useEffect(() => {
        let active = true;
        const timer = setTimeout(async () => {
            try {
                setLoading(true);
                const response = await api.getAllVendor(`?search=${encodeURIComponent(search)}&page=1&status=APPROVED&limit=100&sortBy=fullName&order=asc`);
                if (active) setVendors(response?.data?.data || []);
            } catch (err) {
                console.log(err);
                if (active) toast.error(err?.response?.data?.message || 'Failed to load vendors');
            } finally {
                if (active) setLoading(false);
            }
        }, 300);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [search]);

    const toggleVendor = (id) => {
        setSelectedVendors((prev) => prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]);
    };

    const allVisibleSelected = vendors.length > 0 && vendors.every((v) => selectedVendors.includes(v?._id));

    const toggleAllVisible = (checked) => {
        const visibleIds = vendors.map((v) => v?._id);
        setSelectedVendors((prev) => checked
            ? Array.from(new Set([...prev, ...visibleIds]))
            : prev.filter((id) => !visibleIds.includes(id)));
    };

    const handleSend = async () => {
        if (!selectedVendors.length) {
            toast.error('Please select at least one vendor');
            return;
        }
        setSending(true);
        let success = 0;
        let failed = 0;
        const partList = parts.map((part) => ({
            productNumber: part?.productNumber,
            quantity: part?.quantity,
            manufacturer: part?.manufacturer,
        }));
        // One request per vendor so the vendor gets a single notification for all parts
        for (const vendorId of selectedVendors) {
            try {
                const response = await api.sendEnquiry({
                    enquiryType,
                    enquiryId,
                    vendorId,
                    parts: partList,
                    total: 100,
                });
                const sent = response?.data?.data?.sent ?? partList.length;
                success += sent;
                failed += partList.length - sent;
            } catch (err) {
                console.log(err);
                failed += partList.length;
            }
        }
        setSending(false);
        if (success) toast.success(`Enquiry sent for ${success} item${success > 1 ? 's' : ''}`);
        if (failed) toast.error(`${failed} enquir${failed > 1 ? 'ies' : 'y'} could not be sent`);
        if (success) {
            onSent && onSent();
            onClose();
        }
    };

    return (
        <div className={styless.vendorPickerBackdrop} onClick={onClose}>
            <div className={styless.vendorPicker} onClick={(e) => e.stopPropagation()}>
                <div className={styless.vendorPickerHeader}>
                    <div>
                        <h3>Select Vendors</h3>
                        <p>{parts.length} product{parts.length !== 1 ? 's' : ''} selected to send</p>
                    </div>
                    <button type="button" className={styless.vendorPickerClose} aria-label="Close" onClick={onClose}>&times;</button>
                </div>
                <div className={styless.vendorSearch}>
                    <input
                        type="text"
                        placeholder="Search vendor by name, company or email"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                    />
                </div>
                <label className={styless.vendorSelectAll}>
                    <input
                        type="checkbox"
                        className={styless.selectCheckbox}
                        checked={allVisibleSelected}
                        disabled={!vendors.length}
                        onChange={(e) => toggleAllVisible(e.target.checked)}
                    />
                    Select all vendors
                </label>
                <div className={styless.vendorList}>
                    {loading ?
                        <div className={styless.vendorEmpty}>Loading vendors...</div>
                        : vendors.length === 0 ?
                            <div className={styless.vendorEmpty}>No vendors found</div>
                            :
                            vendors.map((vendor) => (
                                <label key={vendor?._id} className={styless.vendorRow}>
                                    <input
                                        type="checkbox"
                                        className={styless.selectCheckbox}
                                        checked={selectedVendors.includes(vendor?._id)}
                                        onChange={() => toggleVendor(vendor?._id)}
                                    />
                                    <div>
                                        <div className={styless.vendorRowName}>{vendor?.fullName || vendor?.companyName || '-'}</div>
                                        <div className={styless.vendorRowMeta}>
                                            {[vendor?.companyName, vendor?.email].filter(Boolean).join(' • ')}
                                        </div>
                                    </div>
                                </label>
                            ))
                    }
                </div>
                <div className={styless.vendorPickerFooter}>
                    <span>{selectedVendors.length} vendor{selectedVendors.length !== 1 ? 's' : ''} selected</span>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button type="button" className={styless.quoteBtn} onClick={onClose} disabled={sending}>Cancel</button>
                        <button type="button" className={styless.addBtn} onClick={handleSend} disabled={sending || !selectedVendors.length}>
                            {sending ? 'Sending...' : 'Send Enquiry'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BomVendorPicker;
